import React, { useState } from "react";
import "tailwindcss/tailwind.css";
import Sidebaradmin from "./Components/Sidebar";
import Navbaradmin from "./Components/navbar";
import {
  AiFillContacts,
  AiFillDollarCircle,
  AiFillEye,
  AiFillShopping,
  AiOutlineArrowDown,
  AiOutlineArrowUp,
  AiOutlineMessage,
  AiOutlineSearch,
} from "react-icons/ai";
import NewUsers from "./Components/NewUsers";
import VisitorsAnalytics from "./Components/VisitorsAnalytics";
import ProfitOfThePeriod from "./Components/ProfitOfThePeriod";

const sampleUsers = [
  {
    name: "John Doe",
    email: "john.doe@example.com",
    joinedDate: "2024-09-10",
    plan: "Pro",
    lastLogin: "2024-09-12",
    paymentMethod: "Credit Card",
    subscriptionStatus: "Active",
    lastMessage: "Can you provide an update?",
    unread: true,
  },
  {
    name: "Jane Smith",
    email: "jane.smith@example.com",
    joinedDate: "2024-09-11",
    plan: "Basic",
    lastLogin: "2024-09-10",
    paymentMethod: "PayPal",
    subscriptionStatus: "Inactive",
    lastMessage: "I need help with my account.",
    unread: false,
  },
  {
    name: "Alice Johnson",
    email: "alice.johnson@example.com",
    joinedDate: "2024-09-12",
    plan: "Standard",
    lastLogin: "2024-09-12",
    paymentMethod: "Credit Card",
    subscriptionStatus: "Active",
    lastMessage: "Thank you for the support!",
    unread: false,
  },
];

const refundReasons = [
  { user: "John Doe", reason: "Service Quality", amount: "$100", status: "Review" },
  { user: "Jane Smith", reason: "Billing Error", amount: "$150", status: "Pending" },
  { user: "Alice Johnson", reason: "Other", amount: "$50", status: "Approved" },
];

const chatHistoryData = {
  "John Doe": [
    { user: "Admin", message: "Please review the latest refund requests." },
    { user: "John Doe", message: "Can you provide an update?" },
  ],
  "Jane Smith": [
    { user: "Jane Smith", message: "I need help with my account." },
    { user: "Admin", message: "Sure, I am checking on that right now." },
  ],
  "Alice Johnson": [
    { user: "Alice Johnson", message: "Thank you for the support!" },
    { user: "Admin", message: "You are welcome!" },
  ],
};

const overviewStats = [
  {
    label: "Total Views",
    value: "3.456K",
    change: "12.8%",
    trend: "up",
    icon: AiFillEye,
    accent: "bg-sky-50 text-sky-600 ring-sky-100",
  },
  {
    label: "Total Profit",
    value: "$45.2K",
    change: "8.4%",
    trend: "up",
    icon: AiFillDollarCircle,
    accent: "bg-emerald-50 text-emerald-600 ring-emerald-100",
  },
  {
    label: "Products",
    value: "2,450",
    change: "2.1%",
    trend: "down",
    icon: AiFillShopping,
    accent: "bg-amber-50 text-amber-600 ring-amber-100",
  },
  {
    label: "Total Users",
    value: "3,456",
    change: "16.2%",
    trend: "up",
    icon: AiFillContacts,
    accent: "bg-indigo-50 text-indigo-600 ring-indigo-100",
  },
];

const healthStats = [
  { label: "Active Users", value: "2,500", helper: "72% of all learners", tone: "emerald" },
  { label: "Non-Subscribers", value: "350", helper: "Ready for retargeting", tone: "rose" },
  { label: "Expired Plans", value: "120", helper: "Renewal pipeline", tone: "amber" },
  { label: "Pending Refunds", value: "$200", helper: "3 open requests", tone: "sky" },
];

const toneClasses = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  rose: "bg-rose-50 text-rose-700 ring-rose-100",
  amber: "bg-amber-50 text-amber-700 ring-amber-100",
  sky: "bg-sky-50 text-sky-700 ring-sky-100",
};

const getInitials = (name) =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

function StatCard({ stat }) {
  const Icon = stat.icon;
  const isPositive = stat.trend === "up";

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500">{stat.label}</p>
          <h3 className="mt-2 text-3xl font-bold tracking-tight text-slate-950">{stat.value}</h3>
        </div>
        <div className={`rounded-lg p-3 ring-1 ${stat.accent}`}>
          <Icon className="text-2xl" />
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
        <span className="text-sm text-slate-500">vs last month</span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold ${
            isPositive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
          }`}
        >
          {isPositive ? <AiOutlineArrowUp /> : <AiOutlineArrowDown />}
          {stat.change}
        </span>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [chatHistory, setChatHistory] = useState(chatHistoryData);

  const handleSendMessage = () => {
    if (selectedUser && newMessage.trim()) {
      setChatHistory((current) => ({
        ...current,
        [selectedUser]: [...current[selectedUser], { user: "Admin", message: newMessage }],
      }));
      setNewMessage("");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen flex-col md:flex-row">
        <Sidebaradmin />

        <div className="flex min-w-0 flex-1 flex-col">
          <Navbaradmin />

          <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
            <section className="mb-6 flex flex-col justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-sky-600">Admin overview</p>
                <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  Dashboard performance
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-500">
                  Track learner growth, revenue, refunds, and recent support activity from one workspace.
                </p>
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                <label className="relative flex-1 sm:w-72">
                  <AiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-slate-400" />
                  <input
                    type="search"
                    placeholder="Search users, plans, refunds"
                    className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
                  />
                </label>
                <button className="h-11 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800">
                  Export Report
                </button>
              </div>
            </section>

            <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {overviewStats.map((stat) => (
                <StatCard key={stat.label} stat={stat} />
              ))}
            </section>

            <section className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
              <VisitorsAnalytics />
              <ProfitOfThePeriod />
            </section>

            <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {healthStats.map((item) => (
                <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-slate-500">{item.label}</p>
                      <p className="mt-2 text-2xl font-bold text-slate-950">{item.value}</p>
                    </div>
                    <div className={`rounded-lg px-3 py-2 text-sm font-semibold ring-1 ${toneClasses[item.tone]}`}>
                      Live
                    </div>
                  </div>
                  <p className="mt-4 border-t border-slate-100 pt-4 text-sm text-slate-500">{item.helper}</p>
                </div>
              ))}
            </section>

            <section className="mb-6">
              <NewUsers users={sampleUsers} />
            </section>

            <section className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-5">
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-950">Refund Requests</h2>
                    <p className="text-sm text-slate-500">Recent customer refund activity</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[420px] text-left text-sm">
                    <thead>
                      <tr className="border-y border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-3 font-semibold">User</th>
                        <th className="px-4 py-3 font-semibold">Reason</th>
                        <th className="px-4 py-3 font-semibold">Amount</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {refundReasons.map((refund) => (
                        <tr key={`${refund.user}-${refund.reason}`} className="text-slate-700">
                          <td className="px-4 py-3 font-medium text-slate-950">{refund.user}</td>
                          <td className="px-4 py-3">{refund.reason}</td>
                          <td className="px-4 py-3">{refund.amount}</td>
                          <td className="px-4 py-3">
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                              {refund.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-3">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="flex items-center gap-2 text-lg font-bold text-slate-950">
                      <AiOutlineMessage className="text-sky-600" />
                      Chat Messages
                    </h2>
                    <p className="text-sm text-slate-500">Support conversations that need admin attention</p>
                  </div>
                </div>

                {selectedUser ? (
                  <div className="flex flex-col">
                    <div className="mb-4 flex items-center justify-between rounded-lg bg-slate-50 p-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sm font-bold text-sky-700">
                          {getInitials(selectedUser)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-950">{selectedUser}</h3>
                          <p className="text-sm text-slate-500">Active conversation</p>
                        </div>
                      </div>
                      <button
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        onClick={() => setSelectedUser(null)}
                      >
                        Back
                      </button>
                    </div>

                    <div className="max-h-80 space-y-3 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4">
                      {chatHistory[selectedUser].map((chat, index) => {
                        const isAdmin = chat.user === "Admin";
                        return (
                          <div key={`${chat.user}-${index}`} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                            <div
                              className={`max-w-[78%] rounded-lg px-4 py-3 shadow-sm ${
                                isAdmin ? "bg-slate-950 text-white" : "border border-slate-200 bg-white text-slate-800"
                              }`}
                            >
                              <p className={`text-xs font-semibold ${isAdmin ? "text-slate-300" : "text-sky-600"}`}>
                                {chat.user}
                              </p>
                              <p className="mt-1 text-sm leading-6">{chat.message}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                      <input
                        type="text"
                        className="h-11 flex-1 rounded-lg border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(event) => setNewMessage(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            handleSendMessage();
                          }
                        }}
                      />
                      <button
                        className="h-11 rounded-lg bg-sky-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700"
                        onClick={handleSendMessage}
                      >
                        Send
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {sampleUsers.map((user) => (
                      <button
                        key={user.email}
                        className="flex w-full items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 text-left transition hover:border-sky-200 hover:bg-sky-50"
                        onClick={() => setSelectedUser(user.name)}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-sm font-bold text-slate-700">
                            {getInitials(user.name)}
                          </div>
                          <div className="min-w-0">
                            <h3 className="truncate font-semibold text-slate-950">{user.name}</h3>
                            <p className="truncate text-sm text-slate-500">{user.lastMessage}</p>
                          </div>
                        </div>
                        {user.unread && (
                          <span className="shrink-0 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                            Unread
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
